import { LinearProgress, Typography } from '@material-ui/core';
import { Stack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import React, { useMemo } from 'react';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { useNullableContext } from 'shared/hooks/useNullableContext';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;
const Content = styled.div`
  flex-grow: 1;
  overflow: auto;
`;

interface ContextProps {
  currentStep: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  handleNext: () => void;
  handlePrevious?: () => void;
}

const Context = React.createContext<ContextProps | null>(null);

export function useDriverPreferencesStepperContext() {
  return useNullableContext('DriverPreferencesStepper', Context);
}

interface Props {
  currentStepIndex?: number;
  steps: Array<{ label: string; component: React.ReactNode }>;
}

export function DriverPreferencesStepper({
  currentStepIndex = 0,
  steps,
}: Props) {
  const [query, setQuery] = useQuery({ step: currentStepIndex });
  const currentStep = Number(query.step) || 0;

  const contextValue = useMemo(() => {
    const handleNext = () => {
      if (currentStep < steps.length - 1) {
        const nextStep = currentStep + 1;
        setQuery({ step: nextStep }, true);
      }
    };

    const handlePrevious = () => {
      if (currentStep > 0) {
        const previousStep = currentStep - 1;
        setQuery({ step: previousStep }, true);
      }
    };

    return {
      currentStep,
      totalSteps: steps.length,
      isFirstStep: currentStep === 0,
      isLastStep: currentStep === steps.length - 1,
      handleNext,
      handlePrevious,
    };
  }, [currentStep, steps.length, setQuery]);

  const CurrentStepComponent = steps[currentStep]?.component;
  const currentStepProgress = useMemo(
    () => ((currentStep + 1) * 100) / steps.length,
    [currentStep, steps.length],
  );

  return (
    <Context.Provider value={contextValue}>
      <Container>
        <Stack space="none">
          <Box
            paddingLeft="small"
            paddingRight="small"
            paddingBottom="xsmall"
            paddingTop="xsmall"
          >
            <Stack space="none">
              <Typography variant="h6" color="primary">
                STEP {currentStep + 1} OF {steps.length}
              </Typography>
              <Typography variant="body1">
                {steps[currentStep]?.label}
              </Typography>
            </Stack>
          </Box>
          <LinearProgress variant="determinate" value={currentStepProgress} />
        </Stack>

        {CurrentStepComponent}
      </Container>
    </Context.Provider>
  );
}

export function DriverPreferencesStepItem({
  actions,
  children,
}: {
  actions: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <>
      <Content>{children}</Content>
      <Box padding="small" borderTopWidth="small" borderTopColor="Silver400">
        {actions}
      </Box>
    </>
  );
}
